import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/features/notifications/notificationsSlice';
import { cn } from '@/lib/utils';

const POLL_INTERVAL = 60_000; // 60 seconds

export function NotificationBell() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { notifications, unreadCount } = useAppSelector((state) => state.notifications);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(() => {
    dispatch(fetchUnreadCount());
    dispatch(fetchNotifications({ limit: 5 }));
  }, [dispatch]);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, POLL_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchData]);

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  const handleNotificationClick = (id: string, read: boolean) => {
    if (!read) {
      dispatch(markNotificationRead(id));
    }
  };

  const recentNotifications = notifications.slice(0, 5);
  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              aria-live="polite"
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-0.5 text-[10px] font-medium text-destructive-foreground"
            >
              {badgeText}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <h4 className="text-sm font-semibold">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={handleMarkAllRead}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>
        <Separator />

        {/* Notification list */}
        {recentNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <BellOff className="mb-2 h-8 w-8 opacity-50" />
            <p className="text-sm">No new notifications</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[300px]">
            <div className="flex flex-col">
              {recentNotifications.map((notification) => (
                <button
                  key={notification._id}
                  type="button"
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                    !notification.read && 'bg-accent/40'
                  )}
                  onClick={() => handleNotificationClick(notification._id, notification.read)}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {!notification.read && (
                      <div className="h-2 w-2 rounded-full bg-blue-500" />
                    )}
                    {notification.read && <div className="h-2 w-2" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm', !notification.read && 'font-semibold')}>
                      {notification.title}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {notification.message}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        )}

        <Separator />
        {/* Footer */}
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-center text-sm"
            onClick={() => navigate('/notifications')}
          >
            View All
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
